<script setup lang="ts">
import { computed } from "vue";
import type { LeaderboardsResponse } from "@shared/types";
import type { CompId } from "@shared/comp";
import Flag from "../common/Flag.vue";

const props = defineProps<{
  prizes: LeaderboardsResponse["sidePrizes"];
  compId: CompId;
}>();

const concededBreakdown = computed(() => props.prizes.sieves.concededBreakdown);

// Comp 2 is the family competition, so its side prizes use gentler labels.
const FAMILY_TITLES: Record<string, string> = {
  dirtyBastards: "Naughty Step",
  whippingBoys: "Wooden Spoon",
};

function joinNames(names: string[]): string {
  if (names.length === 0) return "—";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
}

// Compact form for the runner-up rows: keep them short on big ties.
function tierNames(names: string[]): string {
  if (names.length <= 3) return joinNames(names);
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

const cards = [
  {
    key: "dirtyBastards" as const,
    title: "Dirty Bastards",
    emoji: "🟨",
    unit: "cards",
    tint: "bg-gradient-to-br from-yellow-50 to-amber-100 ring-yellow-300/60",
    accent: "text-amber-700",
  },
  {
    key: "sieves" as const,
    title: "Sieves",
    emoji: "🥅",
    unit: "conceded",
    tint: "bg-gradient-to-br from-sky-50 to-cyan-100 ring-sky-300/60",
    accent: "text-sky-700",
  },
  {
    key: "whippingBoys" as const,
    title: "Whipping Boys",
    emoji: "🥄",
    unit: "pts",
    tint: "bg-gradient-to-br from-stone-50 to-stone-200 ring-stone-300/60",
    accent: "text-stone-700 italic",
  },
];

const titleFor = computed(() => (key: string, fallback: string) =>
  props.compId === 2 ? FAMILY_TITLES[key] ?? fallback : fallback,
);
</script>

<template>
  <section class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
    <div
      v-for="card in cards"
      :key="card.key"
      :class="['relative overflow-hidden rounded-xl ring-1 p-4 flex flex-col gap-1 shadow-sm', card.tint]"
    >
      <div class="flex items-center justify-between">
        <div class="text-xs uppercase tracking-wider font-bold text-slate-700">{{ titleFor(card.key, card.title) }}</div>
        <span class="text-lg select-none" aria-hidden="true">{{ card.emoji }}</span>
      </div>

      <!-- Sieves: per-leader breakdown of goals conceded by each of the 3 teams -->
      <template v-if="card.key === 'sieves' && concededBreakdown && concededBreakdown.length">
        <ul class="flex flex-col gap-1.5">
          <li v-for="leader in concededBreakdown" :key="leader.name">
            <div :class="['text-sm font-semibold leading-tight', card.accent]">{{ leader.name }}</div>
            <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600 tabular mt-0.5">
              <span
                v-for="team in leader.teams"
                :key="team.name"
                class="inline-flex items-center gap-1"
                :title="team.name"
              >
                <Flag :code="team.flag_code" :name="team.name" size="sm" />
                {{ team.goals_against }}
              </span>
            </div>
          </li>
        </ul>
        <div class="text-sm text-slate-600 tabular mt-0.5">
          <span class="font-semibold">{{ props.prizes.sieves.score }}</span> {{ card.unit }}
        </div>
      </template>

      <template v-else>
        <div :class="['text-lg font-semibold leading-tight', card.accent]">
          {{ joinNames(props.prizes[card.key].leaders) }}
        </div>
        <div class="text-sm text-slate-600 tabular">
          <template v-if="card.key === 'dirtyBastards' && props.prizes.dirtyBastards.yellows !== undefined">
            <span class="font-semibold">🟨 {{ props.prizes.dirtyBastards.yellows }}</span>
            ·
            <span class="font-semibold">🟥 {{ props.prizes.dirtyBastards.reds }}</span>
            ·
            <span class="font-semibold">{{ props.prizes.dirtyBastards.score }}</span> cards
          </template>
          <template v-else>
            <span class="font-semibold">{{ props.prizes[card.key].score }}</span> {{ card.unit }}
          </template>
        </div>
      </template>

      <!-- 2nd / 3rd placed chasing pack — small and unobtrusive -->
      <div
        v-if="props.prizes[card.key].runnersUp?.length"
        class="mt-auto pt-1.5 border-t border-black/5 flex flex-col gap-0.5"
      >
        <div
          v-for="(tier, i) in props.prizes[card.key].runnersUp"
          :key="i"
          class="flex items-baseline gap-1.5 text-[11px] leading-tight text-slate-500"
        >
          <span class="w-5 shrink-0 font-semibold tabular text-slate-400">{{ i === 0 ? "2nd" : "3rd" }}</span>
          <span class="truncate">{{ tierNames(tier.names) }}</span>
          <span class="ml-auto shrink-0 font-semibold tabular">{{ tier.score }}</span>
        </div>
      </div>
    </div>
  </section>
</template>
